import React, { useEffect, useState, useContext } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Spinner, Form, Button, Table } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import axios from "axios";
import MyContext from "../../MyContext";
import { PlusCircle, XCircle, Edit2, Trash2 } from "lucide-react";

const EditIngredients = ({ userID }) => {
  const { itemId } = useParams();
  const location = useLocation();
  const itemName = location.state?.name || "Unnamed Item";
  const itemPhoto = location.state?.photo || "";
  const { baseUrl, config } = useContext(MyContext);

  const [loading, setLoading] = useState(true);
  const [ingredients, setIngredients] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);

  const [newIngredients, setNewIngredients] = useState([
    { ingredientName: "", quantity: "", unit: "" },
  ]);

  const addIngredientRow = () => {
    setNewIngredients([...newIngredients, { ingredientName: "", quantity: "", unit: "" }]);
  };

  const removeIngredientRow = (index) => {
    setNewIngredients(newIngredients.filter((_, i) => i !== index));
  };

  const handleInputChange = (index, field, value) => {
    const updated = [...newIngredients];
    updated[index][field] = value;
    setNewIngredients(updated);
  };

  const handleSubmitAll = async () => {
    const hasEmpty = newIngredients.some((ing) => !ing.ingredientName || !ing.quantity || !ing.unit);
    if (hasEmpty) {
      toast.error("Please fill all ingredient fields before submitting.");
      return;
    }

    try {
      const payload = {
        userid: userID,
        itemId,
        ingredients: newIngredients,
      };

      const res = await axios.post(`${baseUrl}/api/Inventory/v1.0/addRecipeIngredient`, payload, config);
      if (res.data.responseCode === "00") {
        toast.success("Ingredients added successfully!");
        fetchIngredients();
        setNewIngredients([{ ingredientName: "", quantity: "", unit: "" }]);
        setShowAddForm(false);
      } else {
        toast.error("Failed to add ingredients.");
      }
    } catch (err) {
      toast.error("An error occurred during ingredient submission.");
    }
  };

  const fetchIngredients = async () => {
    setLoading(true);
    try {
      const payload = { userid: userID, itemId };
      const res = await axios.post(`${baseUrl}/api/Inventory/v1.0/getRecipeIngredient`, payload, config);
      if (res.data.responseCode === "00") {
        setIngredients(res.data.response);
        setShowAddForm(false); // don't auto-show form when ingredients exist
      } else {
        setIngredients([]);
        setShowAddForm(true); // auto-show form when none found
      }
    } catch (err) {
      toast.error("Failed to fetch ingredients.");
      setIngredients([]);
      setShowAddForm(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIngredients();
  }, []);

  return (
    <div className="p-4" style={{ fontFamily: "Nunito, sans-serif" }}>
      <ToastContainer />
      <div className="d-flex align-items-center mb-4">
        <div style={{ width: "100px", height: "100px", borderRadius: "10px", overflow: "hidden" }}>
          <img src={itemPhoto} alt={itemName} className="w-100 h-100 object-fit-cover" />
        </div>
        <div className="ms-3">
          <h4 className="fw-bold">{itemName}</h4>
          <small className="text-muted">Item ID: {itemId}</small>
        </div>
      </div>

      {loading ? (
        <div className="text-center p-5">
          <Spinner animation="border" variant="primary" /> 
        </div>
      ) : (
        <>
          {ingredients.length > 0 ? (
            <>
              <h5 className="fw-semibold mb-3">Existing Ingredients</h5>
              <Table bordered responsive hover className="shadow-sm">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Ingredient</th>
                    <th>Quantity</th>
                    <th>Unit</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {ingredients.map((ing, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td>{ing.ingredient_name}</td>
                      <td>{ing.quantity}</td>
                      <td>{ing.unit}</td>
                      <td>
                        <Button variant="outline-primary" size="sm" className="me-2">
                          <Edit2 size={14} /> Edit
                        </Button>
                        <Button variant="outline-danger" size="sm">
                          <Trash2 size={14} /> Remove
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {!showAddForm && (
                <div className="text-end mt-3">
                  <Button variant="success" onClick={() => setShowAddForm(true)}>
                    <PlusCircle size={16} className="me-1" />
                    Add More
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center p-4 border rounded bg-light">
              <p className="mb-3">No ingredients found for this item.</p>
              <p className="text-muted">Please start by adding new ingredients below.</p>
            </div>
          )}
        </>
      )}

      {showAddForm && (
        <>
          <hr className="mt-5 mb-3" />
          <h5 className="fw-semibold mb-3">Add Ingredients</h5>
          {newIngredients.map((ing, index) => (
            <div className="row g-3 align-items-end mb-3" key={index}>
              <div className="col-md-4">
                <Form.Label>Ingredient</Form.Label>
                <Form.Control
                  type="text"
                  value={ing.ingredientName}
                  placeholder="e.g. Tomato"
                  onChange={(e) => handleInputChange(index, "ingredientName", e.target.value)}
                />
              </div>
              <div className="col-md-3">
                <Form.Label>Quantity</Form.Label>
                <Form.Control
                  type="text"
                  value={ing.quantity}
                  placeholder="e.g. 200"
                  onChange={(e) => handleInputChange(index, "quantity", e.target.value)}
                />
              </div>
              <div className="col-md-3">
                <Form.Label>Unit</Form.Label>
                <Form.Control
                  type="text"
                  value={ing.unit}
                  placeholder="e.g. gm/ml"
                  onChange={(e) => handleInputChange(index, "unit", e.target.value)}
                />
              </div>
              <div className="col-md-2 text-end">
                <Button
                  variant="danger"
                  onClick={() => removeIngredientRow(index)}
                  disabled={newIngredients.length === 1}
                >
                  <XCircle size={16} className="me-1" />
                  Remove
                </Button>
              </div>
            </div>
          ))}

          <div className="d-flex gap-3 mt-3">
            <Button variant="outline-primary" onClick={addIngredientRow}>
              + Add Another Row
            </Button>
            <Button variant="success" onClick={handleSubmitAll}>
              Submit Ingredients
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default EditIngredients;